import React, { useEffect, useState, useRef, useCallback } from 'react';
import DrawingCard from './DrawingCard.js';
import './DrawingStrip.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const PAGE_SIZE = 20;

export default function DrawingStrip({ title, icon, sort, period }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const spinnerTimerRef = useRef(null);
    const [hasMore, setHasMore] = useState(true);
    const [everLoaded, setEverLoaded] = useState(false);
    const offsetRef = useRef(0);
    const scrollRef = useRef(null);
    // Track current sort+period so in-flight fetches for stale params are ignored
    const currentKey = `${sort}|${period}`;
    const keyRef = useRef(currentKey);
    keyRef.current = currentKey;

    // Only show spinner if loading takes >100ms
    useEffect(() => {
        if (loading) {
            spinnerTimerRef.current = setTimeout(() => setShowSpinner(true), 100);
        } else {
            clearTimeout(spinnerTimerRef.current);
            setShowSpinner(false);
        }
        return () => clearTimeout(spinnerTimerRef.current);
    }, [loading]);

    // When sort/period changes, fetch fresh page-1 and replace items atomically
    useEffect(() => {
        setLoading(true);
        setHasMore(true);
        offsetRef.current = 0;
        const myKey = `${sort}|${period}`;
        const params = new URLSearchParams({
            sort,
            limit: String(PAGE_SIZE),
            offset: '0',
        });
        if (period && period !== 'all') params.set('period', period);

        fetch(`${API_URL}/api/gallery?${params}`)
            .then(r => r.ok ? r.json() : [])
            .then(data => {
                if (keyRef.current !== myKey) return; // stale
                if (data.length < PAGE_SIZE) setHasMore(false);
                setItems(data);
                setEverLoaded(true);
                offsetRef.current = data.length;
                // Reset scroll position
                if (scrollRef.current) scrollRef.current.scrollLeft = 0;
            })
            .catch(() => { if (keyRef.current === myKey) setHasMore(false); })
            .finally(() => { if (keyRef.current === myKey) setLoading(false); });
    }, [sort, period]);

    const fetchMore = useCallback(() => {
        if (loading || !hasMore) return;
        setLoading(true);
        const myKey = keyRef.current;
        const params = new URLSearchParams({
            sort,
            limit: String(PAGE_SIZE),
            offset: String(offsetRef.current),
        });
        if (period && period !== 'all') params.set('period', period);

        fetch(`${API_URL}/api/gallery?${params}`)
            .then(r => r.ok ? r.json() : [])
            .then(data => {
                if (keyRef.current !== myKey) return; // stale
                if (data.length < PAGE_SIZE) setHasMore(false);
                setItems(prev => {
                    const ids = new Set(prev.map(d => d.id));
                    const fresh = data.filter(d => !ids.has(d.id));
                    return [...prev, ...fresh];
                });
                offsetRef.current += data.length;
            })
            .catch(() => { if (keyRef.current === myKey) setHasMore(false); })
            .finally(() => { if (keyRef.current === myKey) setLoading(false); });
    }, [sort, period, loading, hasMore]);

    // Scroll-end detection for load-more
    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 200) {
            fetchMore();
        }
    }, [fetchMore]);

    // Never fully unmount — keep the strip in DOM to avoid layout shift.
    // Only hide if we've fetched at least once and got zero results.
    if (everLoaded && items.length === 0 && !loading) return null;

    return (
        <div className="DrawingStrip">
            {title && (
                <div className="DrawingStrip-header">
                    {icon && <span className="DrawingStrip-icon">{icon}</span>}
                    <h3 className="DrawingStrip-title">{title}</h3>
                </div>
            )}
            <div
                className="DrawingStrip-scroll"
                ref={scrollRef}
                onScroll={handleScroll}
            >
                {items.map(d => (
                    <DrawingCard key={d.id} drawing={d} />
                ))}
                {showSpinner && (
                    <div className="DrawingStrip-loading">
                        <span className="DrawingStrip-spinner" />
                    </div>
                )}
            </div>
        </div>
    );
}
